import React, { useState, useEffect } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Brain, 
  Heart, 
  Zap, 
  TrendingUp, 
  CheckCircle, 
  ArrowRight,
  Play,
  Users,
  Star,
  Shield
} from 'lucide-react';
import { trackGA4Event } from '../utils';

export default function EqUpLandingPage() {
  const [isStickyVisible, setIsStickyVisible] = useState(false);

  // Track scroll for sticky CTA and page view
  useEffect(() => {
    // Track page view for Eq Up landing page
    trackGA4Event('page_view', {
      event_category: 'engagement',
      event_label: 'eq_up_landing_page',
      page_location: window.location.href,
      page_title: 'Eq Up Landing Page'
    });

    const handleScroll = () => {
      const scrollTop = window.scrollY;
      setIsStickyVisible(scrollTop > 800);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleStartTrial = () => {
    // Track GA4 event for Eq Up trial start
    trackGA4Event('eq_up_trial_start', {
      event_category: 'conversion',
      event_label: 'eq_up_landing_page',
      page_location: window.location.href,
      page_title: 'Eq Up Landing Page',
      value: 1
    });
    
    // Redirect to downloads page for now (can be changed to actual trial signup)
    window.open('/downloads', '_blank');
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 lg:py-32">
          <div className="text-center">
            <h1 className="text-5xl lg:text-7xl font-black mb-6 leading-tight">
              <span className="bg-gradient-to-r from-blue-400 to-cyan-400 bg-clip-text text-transparent">
                EQ Up,
              </span>
              <br />
              <span className="text-white">
                Drama Down
              </span>
            </h1>
            
            <p className="text-xl lg:text-2xl text-slate-300 mb-12 max-w-4xl mx-auto font-medium">
              Commit 20 minutes to make sense of your relationship, start connecting deeply, and make her crave you.
            </p>
            
            <div className="flex justify-center mb-16">
              <Button 
                onClick={handleStartTrial}
                size="lg" 
                className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-12 py-6 text-xl font-bold group transition-all duration-200 transform hover:scale-105"
              >
                Start Your Free Trial
                <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" />
              </Button>
            </div>
            
            {/* Hero Visual */}
            <div className="relative max-w-4xl mx-auto">
              <div className="relative bg-white/10 backdrop-blur-sm rounded-3xl p-8 border border-white/20">
                <div className="grid md:grid-cols-2 gap-8 items-center">
                  <div className="space-y-6">
                    <div className="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                      <div className="flex items-center mb-3">
                        <Brain className="w-8 h-8 text-blue-400 mr-3" />
                        <h3 className="text-xl font-bold text-white">Emotional Intelligence</h3>
                      </div>
                      <p className="text-slate-300">Read her signals like a pro</p>
                    </div>
                    
                    <div className="bg-gradient-to-r from-green-500/20 to-teal-500/20 backdrop-blur-sm rounded-2xl p-6 border border-white/20">
                      <div className="flex items-center mb-3">
                        <Heart className="w-8 h-8 text-green-400 mr-3" />
                        <h3 className="text-xl font-bold text-white">Deep Connection</h3>
                      </div>
                      <p className="text-slate-300">Make intimacy effortless</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-center">
                    <div className="relative">
                      <div className="w-48 h-48 bg-gradient-to-br from-blue-500/30 to-cyan-500/30 rounded-full flex items-center justify-center backdrop-blur-sm border border-white/20">
                        <div className="w-32 h-32 bg-gradient-to-br from-blue-400 to-cyan-400 rounded-full flex items-center justify-center">
                          <Zap className="w-16 h-16 text-white" />
                        </div>
                      </div>
                      <div className="absolute -top-4 -right-4 w-12 h-12 bg-gradient-to-r from-green-400 to-teal-400 rounded-full flex items-center justify-center">
                        <TrendingUp className="w-6 h-6 text-white" />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Problem Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Stop Guessing. Start Connecting.
            </h2>
            
            <div className="max-w-4xl mx-auto space-y-6 text-lg text-slate-700">
              <p className="font-medium">
                "She's been asking for more intimacy. You didn't know what to do. You're not alone."
              </p>
              <p>
                Most men aren't taught how to decode her signals — which leads to frustration, missed connection, and a dead bedroom.
              </p>
            </div>
          </div>
          
          {/* Problem Visual */}
          <div className="max-w-4xl mx-auto">
            <Card className="border-2 border-slate-200 shadow-xl">
              <CardContent className="p-12 text-center">
                <div className="w-24 h-24 bg-slate-200 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Users className="w-12 h-12 text-slate-600" />
                </div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">The Confusion is Real</h3>
                <p className="text-lg text-slate-600 max-w-2xl mx-auto">
                  You want to connect, but you're stuck reading mixed signals, second-guessing yourself, and feeling like you're speaking different languages.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* App Experience Preview */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Experience the <span className="text-blue-500">WeSonder App</span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              See how WeSonder transforms your relationship through AI-powered insights and personalized coaching
            </p>
          </div>
          
          {/* App Interface Preview - Deep Personality Analysis */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
            {/* Left Side - Feature Description */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                  <Heart className="w-6 h-6 mr-3 text-green-500" />
                  Deep Personality Analysis
                </h3>
                <p className="text-lg text-slate-600 mb-4">
                  Discover your communication style, attachment patterns, and relationship dynamics through AI analysis of your conversations.
                </p>
                <div className="space-y-2 text-slate-700">
                  <p className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />Communication style breakdown</p>
                  <p className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />Attachment pattern analysis</p>
                  <p className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />Emotional intelligence insights</p>
                </div>
              </div>
            </div>
            
            {/* Right Side - Personality Insights Mockup */}
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm mx-auto">
                {/* Phone Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
                  <div className="flex items-center space-x-2">
                    <img 
                      src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6992dc80a_WeSonder1024x1024.png" 
                      alt="WeSonder Logo" 
                      className="w-6 h-6 rounded-full"
                    />
                    <span className="font-bold text-slate-900">WeSonder</span>
                  </div>
                  <div className="w-4 h-4 bg-slate-300 rounded"></div>
                </div>
                
                {/* Personality Card */}
                <div className="bg-blue-50 rounded-2xl p-4 mb-4">
                  <h4 className="font-bold text-slate-900 mb-2 text-sm">You in One Sentence</h4>
                  <p className="text-xs text-slate-700">A gentle yet steadfast soul, you expertly navigate life's logistics with a heart attuned to service, striving for harmony while yearning for genuine appreciation.</p>
                </div>
                
                {/* Personality Sections */}
                <div className="space-y-3">
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-blue-500 rounded mr-2"></div>
                        <span className="text-sm font-medium text-slate-900">Communication Style</span>
                      </div>
                      <div className="w-4 h-4 bg-slate-300 rounded"></div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-green-500 rounded mr-2"></div>
                        <span className="text-sm font-medium text-slate-900">Attachment Pattern</span>
                      </div>
                      <div className="w-4 h-4 bg-slate-300 rounded"></div>
                    </div>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center">
                        <div className="w-4 h-4 bg-purple-500 rounded mr-2"></div>
                        <span className="text-sm font-medium text-slate-900">Conflict Style</span>
                      </div>
                      <div className="w-4 h-4 bg-slate-300 rounded"></div>
                    </div>
                  </div>
                </div>
                
                {/* Bottom Navigation - 4 Tabs */}
                <div className="flex justify-around pt-4 border-t border-slate-200 mt-6">
                  <div className="text-center">
                    <div className="w-6 h-6 bg-slate-300 rounded mx-auto mb-1"></div>
                    <span className="text-xs text-slate-500">Home</span>
                  </div>
                  <div className="text-center">
                    <div className="w-6 h-6 bg-blue-500 rounded mx-auto mb-1 flex items-center justify-center">
                      <Heart className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs text-blue-500 font-medium">Me</span>
                  </div>
                  <div className="text-center">
                    <div className="w-6 h-6 bg-slate-300 rounded mx-auto mb-1"></div>
                    <span className="text-xs text-slate-500">Relationships</span>
                  </div>
                  <div className="text-center">
                    <div className="w-6 h-6 bg-slate-300 rounded mx-auto mb-1"></div>
                    <span className="text-xs text-slate-500">Coach</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* App Interface Preview - Understand Her & Your Dynamic */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mt-16 mb-16">
            {/* Left Side - Relationship Analysis Mockup */}
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm mx-auto">
                {/* Phone Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
                  <div className="flex items-center space-x-2">
                    <img 
                      src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6992dc80a_WeSonder1024x1024.png" 
                      alt="WeSonder Logo" 
                      className="w-6 h-6 rounded-full"
                    />
                    <span className="font-bold text-slate-900">WeSonder</span>
                  </div>
                  <div className="w-4 h-4 bg-slate-300 rounded"></div>
                </div>
                
                {/* Contact Info */}
                <div className="mb-4">
                  <p className="text-xs text-slate-500">+1 (555) 123-4567</p>
                </div>
                
                {/* Relationship Features */}
                <div className="space-y-4">
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-3">
                          <Heart className="w-3 h-3 text-blue-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-900">Understand Them</span>
                      </div>
                      <div className="w-4 h-4 bg-slate-300 rounded"></div>
                    </div>
                    <p className="text-xs text-slate-600">Deep personality analysis of your partner</p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="w-6 h-6 bg-green-100 rounded-full flex items-center justify-center mr-3">
                          <Users className="w-3 h-3 text-green-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-900">The Dynamic</span>
                      </div>
                      <div className="w-4 h-4 bg-slate-300 rounded"></div>
                    </div>
                    <p className="text-xs text-slate-600">Analyze your relationship patterns together</p>
                  </div>
                  
                  <div className="bg-slate-50 rounded-xl p-4">
                    <div className="flex items-center justify-between mb-2">
                      <div className="flex items-center">
                        <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-3">
                          <Zap className="w-3 h-3 text-purple-600" />
                        </div>
                        <span className="text-sm font-medium text-slate-900">Communicate Better</span>
                      </div>
                      <div className="w-4 h-4 bg-slate-300 rounded"></div>
                    </div>
                    <p className="text-xs text-slate-600">Improve your communication as a couple</p>
                  </div>
                </div>
                
                {/* Bottom Navigation - 4 Tabs */}
                <div className="flex justify-around pt-4 border-t border-slate-200 mt-6">
                  <div className="text-center">
                    <div className="w-6 h-6 bg-slate-300 rounded mx-auto mb-1"></div>
                    <span className="text-xs text-slate-500">Home</span>
                  </div>
                  <div className="text-center">
                    <div className="w-6 h-6 bg-slate-300 rounded mx-auto mb-1"></div>
                    <span className="text-xs text-slate-500">Me</span>
                  </div>
                  <div className="text-center">
                    <div className="w-6 h-6 bg-blue-500 rounded mx-auto mb-1 flex items-center justify-center">
                      <Users className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs text-blue-500 font-medium">Relationships</span>
                  </div>
                  <div className="text-center">
                    <div className="w-6 h-6 bg-slate-300 rounded mx-auto mb-1"></div>
                    <span className="text-xs text-slate-500">Coach</span>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Right Side - Feature Description */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                  <Users className="w-6 h-6 mr-3 text-purple-500" />
                  Understand Her & Your Dynamic
                </h3>
                <p className="text-lg text-slate-600 mb-4">
                  Get deep insights into your partner's personality and understand the unique dynamics of your relationship as a couple.
                </p>
                <div className="space-y-4">
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center">
                      <Heart className="w-4 h-4 mr-2 text-blue-500" />
                      Understand Her Deeply
                    </h4>
                    <p className="text-sm text-slate-600">Discover her communication style, emotional needs, and how she processes conflict</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center">
                      <Users className="w-4 h-4 mr-2 text-green-500" />
                      Analyze Your Dynamic
                    </h4>
                    <p className="text-sm text-slate-600">Understand how you work together as a couple and identify patterns that strengthen or weaken your bond</p>
                  </div>
                  
                  <div className="bg-white rounded-xl p-4 shadow-sm">
                    <h4 className="font-semibold text-slate-900 mb-2 flex items-center">
                      <Zap className="w-4 h-4 mr-2 text-purple-500" />
                      Communicate Better
                    </h4>
                    <p className="text-sm text-slate-600">Learn the specific communication strategies that work best for your unique relationship dynamic</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* App Interface Preview - Real-Time AI Coaching */}
          <div className="grid lg:grid-cols-2 gap-12 items-center mt-16">
            {/* Left Side - Feature Description */}
            <div className="space-y-8">
              <div>
                <h3 className="text-2xl font-bold text-slate-900 mb-4 flex items-center">
                  <Brain className="w-6 h-6 mr-3 text-blue-500" />
                  Real-Time AI Coaching
                </h3>
                <p className="text-lg text-slate-600 mb-4">
                  Get instant, personalized advice based on your actual communication patterns. No more guessing what to say.
                </p>
                <div className="space-y-2 text-slate-700">
                  <p className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />24/7 relationship coaching</p>
                  <p className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />Personalized conversation analysis</p>
                  <p className="flex items-center"><CheckCircle className="w-4 h-4 text-green-500 mr-2" />Instant communication guidance</p>
                </div>
              </div>
            </div>
            
            {/* Right Side - App Interface Mockup */}
            <div className="relative">
              <div className="bg-white rounded-3xl shadow-2xl p-6 max-w-sm mx-auto">
                {/* Phone Header */}
                <div className="flex items-center justify-between mb-6 pb-4 border-b border-slate-200">
                  <div className="flex items-center space-x-2">
                    <img 
                      src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/6992dc80a_WeSonder1024x1024.png" 
                      alt="WeSonder Logo" 
                      className="w-6 h-6 rounded-full"
                    />
                    <span className="font-bold text-slate-900">WeSonder</span>
                  </div>
                  <div className="w-4 h-4 bg-slate-300 rounded"></div>
                </div>
                
                {/* Chat Interface */}
                <div className="space-y-4 mb-6">
                  <div className="bg-blue-50 rounded-2xl p-4">
                    <p className="text-sm text-slate-700">Hi! 👋 I've analyzed your communication patterns and I'm excited to help you understand your relationship better. What's on your mind today?</p>
                  </div>
                  
                  <div className="bg-slate-100 rounded-2xl p-4 ml-8">
                    <p className="text-sm text-slate-700">I've been feeling disconnected from my partner lately. I don't know how to bridge the gap.</p>
                  </div>
                  
                  <div className="bg-blue-50 rounded-2xl p-4">
                    <p className="text-sm text-slate-700">I can see from your communication style that you're solution-oriented. Your partner might need emotional validation first before practical solutions. Try asking: "How are you feeling about this?"</p>
                  </div>
                </div>
                
                {/* Bottom Navigation - 4 Tabs */}
                <div className="flex justify-around pt-4 border-t border-slate-200">
                  <div className="text-center">
                    <div className="w-6 h-6 bg-slate-300 rounded mx-auto mb-1"></div>
                    <span className="text-xs text-slate-500">Home</span>
                  </div>
                  <div className="text-center">
                    <div className="w-6 h-6 bg-slate-300 rounded mx-auto mb-1"></div>
                    <span className="text-xs text-slate-500">Me</span>
                  </div>
                  <div className="text-center">
                    <div className="w-6 h-6 bg-slate-300 rounded mx-auto mb-1"></div>
                    <span className="text-xs text-slate-500">Relationships</span>
                  </div>
                  <div className="text-center">
                    <div className="w-6 h-6 bg-blue-500 rounded mx-auto mb-1 flex items-center justify-center">
                      <Users className="w-3 h-3 text-white" />
                    </div>
                    <span className="text-xs text-blue-500 font-medium">Coach</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Turn Confusion into Connection
            </h2>
            
            <div className="max-w-4xl mx-auto space-y-6 text-lg text-slate-700">
              <p className="font-medium">
                "WeSonder gives you a proven system to finally understand her."
              </p>
              <p>
                Feel like a mind reader. Connect instantly. Make intimacy effortless.
              </p>
            </div>
          </div>
          
          {/* Benefits Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="border-2 border-blue-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Brain className="w-8 h-8 text-blue-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">Understand Her</h3>
                <p className="text-slate-600">Read her emotional signals and decode what she really means</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-green-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Zap className="w-8 h-8 text-green-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">Connect Instantly</h3>
                <p className="text-slate-600">Respond the way she's waiting for and build deeper intimacy</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-purple-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Heart className="w-8 h-8 text-purple-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">Boost Your Sex Life</h3>
                <p className="text-slate-600">Emotional connection = better sex. It's that simple.</p>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-orange-200 shadow-lg hover:shadow-xl transition-shadow">
              <CardContent className="p-8 text-center">
                <div className="w-16 h-16 bg-orange-100 rounded-full flex items-center justify-center mx-auto mb-6">
                  <TrendingUp className="w-8 h-8 text-orange-600" />
                </div>
                <h3 className="text-xl font-bold text-slate-900 mb-4">Track Your Wins</h3>
                <p className="text-slate-600">See measurable improvement in your relationship</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* How It Works Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 to-blue-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-6">
              EQ Up → Drama Down
            </h2>
            <p className="text-xl text-slate-300 max-w-3xl mx-auto">
              A proven 3-step system that transforms your relationship.
            </p>
          </div>
          
          {/* Steps */}
          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-blue-500 to-cyan-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold">1</span>
              </div>
              <h3 className="text-2xl font-bold mb-4">Decode Your Relationship</h3>
              <p className="text-slate-300 text-lg">
                Analyze your communication patterns and understand what's really happening between you two
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-teal-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold">2</span>
              </div>
              <h3 className="text-2xl font-bold mb-4">Understand Your Personality</h3>
              <p className="text-slate-300 text-lg">
                Discover your communication style, attachment patterns, and how they affect your relationship
              </p>
            </div>
            
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-6">
                <span className="text-3xl font-bold">3</span>
              </div>
              <h3 className="text-2xl font-bold mb-4">Improve Your Intimacy</h3>
              <p className="text-slate-300 text-lg">
                Apply personalized insights to build deeper connection and create the intimacy you both crave
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-6">
              Men Who Use WeSonder See Results
            </h2>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-lg text-slate-700 mb-6 italic">
                  "I never realized how clueless I was — now she actually feels seen, and so do I."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mr-4">
                    <Users className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Mike, 34</p>
                    <p className="text-slate-600">Married 8 years</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-lg text-slate-700 mb-6 italic">
                  "20 minutes changed everything — better connection, better sex."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mr-4">
                    <Users className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Jake, 41</p>
                    <p className="text-slate-600">In relationship 3 years</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            <Card className="border-2 border-slate-200 shadow-lg">
              <CardContent className="p-8">
                <div className="flex items-center mb-4">
                  {[...Array(5)].map((_, i) => (
                    <Star key={i} className="w-5 h-5 text-yellow-400 fill-current" />
                  ))}
                </div>
                <p className="text-lg text-slate-700 mb-6 italic">
                  "Feels like a cheat code for relationships."
                </p>
                <div className="flex items-center">
                  <div className="w-12 h-12 bg-slate-200 rounded-full flex items-center justify-center mr-4">
                    <Users className="w-6 h-6 text-slate-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-slate-900">Ryan, 29</p>
                    <p className="text-slate-600">Dating 1 year</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Bottom CTA Section */}
      <section className="py-20 bg-gradient-to-br from-blue-900 via-slate-900 to-blue-800 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl lg:text-5xl font-bold mb-6">
            Stop Guessing. Start Feeling.
          </h2>
          
          <p className="text-xl text-slate-300 mb-8 max-w-3xl mx-auto">
            EQ Up, Drama Down. Transform your relationship.
          </p>
          
          <div className="flex justify-center mb-8">
            <Button 
              onClick={handleStartTrial}
              size="lg" 
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-12 py-6 text-xl font-bold group transition-all duration-200 transform hover:scale-105"
            >
              Start Your Free Trial
              <ArrowRight className="w-6 h-6 ml-3 group-hover:translate-x-1 transition-transform" />
            </Button>
          </div>
          
          <div className="flex items-center justify-center space-x-8 text-sm text-slate-400">
            <div className="flex items-center">
              <Shield className="w-5 h-5 mr-2" />
              100% discreet
            </div>
            <div className="flex items-center">
              <CheckCircle className="w-5 h-5 mr-2" />
              No credit card required
            </div>
            <div className="flex items-center">
              <Play className="w-5 h-5 mr-2" />
              Instant access
            </div>
          </div>
        </div>
      </section>

      {/* Sticky CTA */}
      {isStickyVisible && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-slate-200 shadow-lg z-50 p-4">
          <div className="max-w-7xl mx-auto flex items-center justify-between">
            <div className="flex-1">
              <p className="font-semibold text-slate-900">Ready to transform your relationship?</p>
              <p className="text-sm text-slate-600">Start your free trial now</p>
            </div>
            <Button 
              onClick={handleStartTrial}
              className="bg-gradient-to-r from-blue-500 to-cyan-500 hover:from-blue-600 hover:to-cyan-600 text-white px-8 py-3 font-bold"
            >
              Start Free Trial
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
